import {createContext, useCallback, useContext, useMemo, useState, type ReactNode} from 'react';

export type SiteStatusTone = 'success' | 'warning' | 'neutral';

export interface Site {
  id: string;
  name: string;
  domain: string;
  url?: string;
  company: string;
  status: string;
  statusTone: SiteStatusTone;
  theme: string;
  products: number;
  pages: number;
  images: number;
  version: string;
  lastPublished: string;
  initial: string;
}

export const sites: Site[] = [
  {
    id: 'wlacc',
    name: 'WLACC 官网',
    domain: 'wlacc.com',
    url: 'https://wlacc.com',
    company: '义乌微澜饰品有限公司',
    status: '已上线',
    statusTone: 'success',
    theme: 'Storefront',
    products: 24,
    pages: 8,
    images: 118,
    version: 'V4',
    lastPublished: '2026 年 8 月 22 日 · 16:40',
    initial: 'W',
  },
  {
    id: 'aster-living',
    name: 'Aster Living',
    domain: '尚未连接域名',
    company: 'Aster Home Studio',
    status: '草稿',
    statusTone: 'warning',
    theme: 'Editorial',
    products: 0,
    pages: 3,
    images: 0,
    version: 'Draft',
    lastPublished: '尚未发布',
    initial: 'A',
  },
  {
    id: 'northline-goods',
    name: 'Northline Goods',
    domain: '尚未连接域名',
    company: 'Northline Trading',
    status: '资料准备中',
    statusTone: 'neutral',
    theme: '尚未选择',
    products: 0,
    pages: 0,
    images: 0,
    version: '—',
    lastPublished: '尚未同步',
    initial: 'N',
  },
];

const defaultSiteId = 'wlacc';

interface SiteContextValue {
  sites: Site[];
  currentSite: Site;
  selectSite: (siteId: string) => void;
  getSite: (siteId: string | undefined) => Site | undefined;
}

const SiteContext = createContext<SiteContextValue | null>(null);

function getInitialSiteId() {
  if (typeof window === 'undefined') return defaultSiteId;
  try {
    const stored = window.localStorage.getItem('wj-active-site');
    return sites.some((site) => site.id === stored) ? stored || defaultSiteId : defaultSiteId;
  } catch {
    return defaultSiteId;
  }
}

export function SiteProvider({children}: {children: ReactNode}) {
  const [activeSiteId, setActiveSiteId] = useState(getInitialSiteId);
  const currentSite = useMemo(() => sites.find((site) => site.id === activeSiteId) || sites[0], [activeSiteId]);

  const selectSite = useCallback((siteId: string) => {
    if (!sites.some((site) => site.id === siteId)) return;
    setActiveSiteId(siteId);
    try {
      window.localStorage.setItem('wj-active-site', siteId);
    } catch {
      // The in-memory selection remains usable when browser storage is unavailable.
    }
  }, []);

  const getSite = useCallback((siteId: string | undefined) => sites.find((site) => site.id === siteId), []);
  const value = useMemo(() => ({sites, currentSite, selectSite, getSite}), [currentSite, getSite, selectSite]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite() {
  const context = useContext(SiteContext);
  if (!context) throw new Error('useSite must be used inside SiteProvider');
  return context;
}
